import { useEffect, useState } from "react";
import DB_PATH from "../DB_PATH";
import { firestore } from "../firebase";
import { MarketingContext } from "../../types/MarketingContext";

const CONTEXT_DOC = "context";

function ref(communityId: string) {
  return firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection(DB_PATH.MARKETING)
    .doc(CONTEXT_DOC);
}

type Options = {
  communityId?: string;
};

/**
 * Live-subscribe to the community's marketing/brand context
 * (Community/{id}/Marketing/context). Returns null until loaded or when there
 * is no communityId. Mirrors useDistribution.
 */
export const useMarketingContext = ({ communityId }: Options = {}) => {
  const [context, setContext] = useState<MarketingContext | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(communityId));

  useEffect(() => {
    if (!communityId) {
      setContext(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = ref(communityId).onSnapshot(
      (doc) => {
        setContext(doc.exists ? (doc.data() as MarketingContext) : {});
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching marketing context", error);
        setContext(null);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [communityId]);

  return { context, loading };
};

/**
 * Merge-write a partial marketing context. Only the fields you pass are
 * changed, so partial saves never wipe the rest of the document.
 */
export const saveMarketingContext = async (
  communityId: string,
  partial: Partial<MarketingContext>
): Promise<void> => {
  if (!communityId) {
    throw new Error("communityId is required to save marketing context");
  }
  try {
    await ref(communityId).set(
      { ...partial, updatedAt: new Date().toISOString() },
      { merge: true }
    );
  } catch (error) {
    console.error("Error saving marketing context", error);
    throw error;
  }
};
